import { MapPinSprite, pinMarkerCentreLiftPx, useDPRScale } from './MapPin'
import { pinLabelColors, shouldShowPinLabel } from '../lib/mapAppearance'
import { pinLabelCssPx } from '../lib/geo'
import { MAP_MAX_ZOOM } from '@wehunt/map'
import type { MapObject } from '../lib/types'
import type { MapAppearance } from '../lib/mapAppearance'
import { printPinLabelStyle, type PrintPinPreview } from '../lib/printPinPreview'

export interface PinMarkerContentProps {
  object: MapObject
  appearance: MapAppearance
  /** Live map zoom — drives label font size. */
  zoom: number
  /**
   * From the appearance-panel presence rule: a surface that renders the panel
   * follows its switch, and a surface without one shows no labels at all —
   * it would have no way to turn them off again.
   */
  pinNamesVisible: boolean
  /**
   * On a print surface: the pin and label size the sheet will carry, in place
   * of the Pin size setting and the zoom-driven label size, with the label drawn
   * as the server prints it and the pin at full opacity (see `printPinPreview`). The label then shows when
   * the label slider is above 0, whatever the pin-names switch says, because the
   * slider is what decides whether the sheet carries names.
   */
  printPreview?: PrintPinPreview
}

// MapPinSprite's glyph occupies only the bottom of its legacy 56×47
// coordinate box (tip anchored at (28,47)): the bg image is 20 wide, 20.83
// tall at net scale 1 (21 here for a safe crop), and the top ~27 units are
// empty legacy headroom. GLYPH_W/H below are that visible glyph's own size in
// SVG user units at net scale 1 — fixed CSS-pixel sizing (window-independent)
// matches the "MapLibre-native feel" parity bar the old, window-dependent SVG
// engine didn't have.
const GLYPH_W = 20
const GLYPH_H = 21

/** The DOM content of one map-pin marker: optional name label above the pin
 *  sprite. Anchor the enclosing MapMarker 'bottom' — the bottom-center of this
 *  element is the pin's location: its tip, or the middle of a drawing that has
 *  none (see `pinMarkerCentreLiftPx`). */
export function PinMarkerContent({ object, appearance, zoom, pinNamesVisible, printPreview }: PinMarkerContentProps) {
  const label = pinLabelColors(appearance.labelBackground)
  const fontSize = pinLabelCssPx(zoom, MAP_MAX_ZOOM)
  const showLabel = shouldShowPinLabel({
    pinNamesVisible: printPreview ? printPreview.labelPx > 0 : pinNamesVisible,
    label: object.label,
  })
  // MapPinSprite multiplies its `scale` prop by useDPRScale() internally — a
  // factor (min(1, dpr/3)) that exists to compensate the OLD SVG engine's
  // window-dependent viewBox stretch, which happened to roughly cancel it.
  // This marker's box is fixed CSS pixels with nothing left to cancel, so
  // left alone the sprite renders at ~1/3 size on a dpr-1 desktop (the "pins
  // are invisible" bug). Dividing by the same factor here neutralizes it, so
  // the net on-screen scale is exactly `appearance.pinScale`.
  const dprScale = useDPRScale()
  const pinScale = printPreview?.pinScale ?? appearance.pinScale
  const spriteScale = dprScale > 0 ? pinScale / dprScale : pinScale
  // The sprite is still positioned at its legacy (28,47) tip inside the
  // 56×47 box (unchanged below), but the viewBox is cropped down to just the
  // glyph — scaled by pinScale, since the glyph itself grows with it — so the
  // label stacks directly above the visible pin instead of above the box's
  // empty top headroom, and the marker's own bottom-anchor hugs the glyph.
  const s = pinScale
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
      {showLabel && (
        <span
          style={{
            fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
            fontSize,
            // The page's inherited line-height (~1.4) pads the pill well beyond
            // the glyphs; the old engine drew raw SVG <text> with no line box
            // at all, so the pill hugged the text. 1.2 keeps ascenders and
            // descenders unclipped while matching the legacy pill's tightness
            // (its height was fontSize + 2×2px padding).
            lineHeight: 1.2,
            fontWeight: 600,
            color: label.text,
            // The print draws names at full strength; label opacity is a viewing preference.
            opacity: printPreview ? 1 : appearance.labelOpacity / 100,
            whiteSpace: 'nowrap',
            marginBottom: 2,
            ...(printPreview
              ? printPinLabelStyle(printPreview.labelPx)
              : appearance.labelBackground
              ? {
                  background: label.pillFill,
                  border: `1px solid ${label.pillBorder}`,
                  borderRadius: 4,
                  padding: '1px 5px',
                }
              : { textShadow: `0 0 2px ${label.textHalo}, 0 0 2px ${label.textHalo}` }),
          }}
        >
          {object.label}
        </span>
      )}
      <svg
        width={GLYPH_W * s}
        height={GLYPH_H * s}
        viewBox={`${28 - GLYPH_W / 2 * s} ${47 - GLYPH_H * s} ${GLYPH_W * s} ${GLYPH_H * s}`}
        style={{
          display: 'block',
          overflow: 'visible',
          marginBottom: -pinMarkerCentreLiftPx(object.type, appearance.pinBackground, s),
        }}
      >
        <MapPinSprite
          kind={object.type}
          symbol={object.customSymbol}
          x={28}
          y={47}
          withBackground={appearance.pinBackground}
          cursor="default"
          interactive={false}
          scale={spriteScale}
          color={object.color}
          // The print draws every pin opaque; pin opacity is a viewing preference.
          opacity={printPreview ? 1 : appearance.pinOpacity / 100}
        />
      </svg>
    </div>
  )
}
